import { useEffect, useState } from 'react';

import { Commit, Tag } from 'src/components/types';
import { useIntl } from 'src/hooks/intl';
import { LOADING_STATE, REF_TYPE } from 'src/sections/create-branch/constants';
import { Ref } from 'src/sections/create-branch/types';
import { getRefs } from 'src/sections/create-branch/utils';
import { fetchRefOptions } from 'src/sections/repository/components/ref-selector/utils';
import urls from 'src/sections/repository/urls';
import authRequest, { jsonHeaders } from 'src/utils/fetch';

import messages from '../components/commit-page.i18n';

export type CreateTagRequestData = {
  name: string;
  description: string;
};

export type CreateTagResponseData = {
  success: boolean;
  error: Error;
};

export type CommitSelectorType = {
  loadingState: LOADING_STATE;
  refs: Ref[];
  hasMoreRefs: boolean;
};

export function useTagHooks(commit: Commit): {
  tags: CommitSelectorType;
  createTag: (data: CreateTagRequestData) => Promise<CreateTagResponseData>;
} {
  const intl = useIntl();

  const { hash } = commit;
  const repositoryFullSlug = commit.repository.full_name;
  const [owner, slug] = repositoryFullSlug.split('/');
  const baseUrl = urls.api.v20.tags(owner, slug);

  const commitSelector: CommitSelectorType = {
    loadingState: LOADING_STATE.LOADING,
    refs: [] as Ref[],
    hasMoreRefs: false,
  };
  const [tags, setTags] = useState(commitSelector);

  // Creates a tag and returns errors if they happen
  const createTag = async (data: CreateTagRequestData) => {
    const createRequest = authRequest(baseUrl, {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify({
        name: data.name,
        target: {
          hash: commit.hash,
        },
        message: data.description,
      }),
    });

    const response: Response = await fetch(createRequest);
    if (response.ok) {
      const tag: Tag = await response.json();
      const refTag: Ref = { type: REF_TYPE.TAG, name: tag.name };
      setTags(prevState => ({
        ...prevState,
        refs: tags.refs.concat(refTag),
      }));
      return { success: true, error: null };
    } else {
      const json = await response.json();

      if ('error' in json) {
        return { success: false, error: json.error };
      } else {
        return {
          success: false,
          error: { message: intl.formatMessage(messages.tagCreationError) },
        };
      }
    }
  };

  useEffect(() => {
    // load tags on page load
    const getTags = async () => {
      const query = `?q=target.hash="${hash}"&pagelen=25`;
      const url = `${baseUrl}${query}`;
      try {
        const { preparedRefs, next } = await fetchRefOptions(
          url,
          false,
          '',
          'tag',
          null
        );

        setTags(prevState => ({
          ...prevState,
          refs: getRefs(preparedRefs),
          hasMoreRefs: !!next,
          loadingState: LOADING_STATE.SUCCESS,
        }));
      } catch {
        setTags(prevState => ({
          ...prevState,
          loadingState: LOADING_STATE.ERROR,
        }));
      }
    };
    getTags();
  }, [baseUrl, hash, repositoryFullSlug]);

  return {
    tags,
    createTag,
  };
}
